const SERVER_LINK_SHAPES = Object.freeze({
  TagId: Object.freeze(['single']),
  TagIds: Object.freeze(['csv']),
  Id: Object.freeze(['single', 'registerRows']),
});

const LEGACY_TAG_COLUMNS = Object.freeze([
  'tagNumber',
  'tag',
  'tagNo',
  'tag_number',
  'tag_no',
  'equipment_tag',
]);

/** Reserved row metadata returned only when a grid opts into isolation availability. */
export const ROW_ISOLATION_AVAILABILITY = '__CanIsolate';

/**
 * The v2 compiled source's exact row key, projected as a server-owned machine column so a capable
 * source's rows can address themselves to Spatial Focus. Named here for the SAME reason as
 * `ROW_ISOLATION_AVAILABILITY`: it rides the row payload but is not data, so anything deriving grid
 * columns from row keys must exclude it. A v1/register source is keyed by `Id`, which IS a real
 * column and is deliberately absent from this list.
 */
export const ROW_SPATIAL_KEY = '__RowKey';
export const ROW_SPATIAL_METADATA = Object.freeze([
  ROW_ISOLATION_AVAILABILITY, ROW_SPATIAL_KEY,
  '__SpatialAvailability', '__SpatialReason', '__SpatialActionMode',
]);

function columnNames(availableColumns) {
  return (Array.isArray(availableColumns) ? availableColumns : [])
    .map(column => (typeof column === 'string' ? column : column?.name))
    .filter(name => typeof name === 'string' && name.length > 0);
}

function matchingColumn(columns, requested) {
  if (!requested) return null;
  const normalized = String(requested).toLowerCase();
  return columns.find(column => column.toLowerCase() === normalized) || null;
}

function descriptorForColumn(column, tableName) {
  const normalizedColumn = column.toLowerCase();
  if (normalizedColumn === 'tagids') return { column, shape: 'csv' };
  if (normalizedColumn === 'id' && String(tableName || '').toLowerCase().startsWith('vw_register_')) {
    return { column, shape: 'registerRows' };
  }
  return { column, shape: 'single' };
}

function validatedServerLink(serverTagLink) {
  if (!serverTagLink || typeof serverTagLink !== 'object') return null;
  const column = serverTagLink.column;
  const shape = serverTagLink.shape;
  if (!SERVER_LINK_SHAPES[column]?.includes(shape)) return null;
  return { column, shape };
}

export function resolveTagLink({
  serverTagLink,
  legacyTagColumn,
  tableName,
  availableColumns,
} = {}) {
  if (serverTagLink !== undefined) return validatedServerLink(serverTagLink);

  const columns = columnNames(availableColumns);
  const legacyColumn = matchingColumn(columns, legacyTagColumn);
  const registerId = matchingColumn(columns, 'Id');
  const isRegister = String(tableName || '').toLowerCase().startsWith('vw_register_');
  if (legacyColumn) {
    if (isRegister && legacyColumn.toLowerCase() === 'tagid' && registerId) {
      return { column: registerId, shape: 'registerRows' };
    }
    return descriptorForColumn(legacyColumn, tableName);
  }

  if (isRegister && registerId) {
    return { column: registerId, shape: 'registerRows' };
  }

  const tagIds = matchingColumn(columns, 'TagIds');
  if (tagIds) return { column: tagIds, shape: 'csv' };

  const tagId = matchingColumn(columns, 'TagId');
  if (tagId) return { column: tagId, shape: 'single' };

  const dashboardId = matchingColumn(columns, 'Id');
  if (String(tableName || '').toLowerCase().startsWith('vw_dashboard_') && dashboardId) {
    return { column: dashboardId, shape: 'single' };
  }

  for (const knownColumn of LEGACY_TAG_COLUMNS) {
    const matched = matchingColumn(columns, knownColumn);
    if (matched) return { column: matched, shape: 'single' };
  }

  return null;
}

export function splitTagValues(value) {
  if (value === null || value === undefined) return [];

  const seen = new Set();
  return String(value)
    .split(',')
    .map(tagId => tagId.trim())
    .filter((tagId) => {
      if (!tagId) return false;
      const key = tagId.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

/**
 * One truthful render gate for every row-level isolate action.
 *
 * The server checks the record's exact display destination. A link alone does not prove that
 * destination exists. Unknown availability is not an enabled action, including older responses.
 */
export function rowCanIsolate(row, tagLink) {
  if (!row || !tagLink?.column) return false;

  if (typeof row[ROW_ISOLATION_AVAILABILITY] === 'boolean') {
    if (!row[ROW_ISOLATION_AVAILABILITY]) return false;
    const availability = row.__SpatialAvailability;
    return (['ownElement', 'ownPoint'].includes(availability) && row.__SpatialActionMode === 'source')
      || (availability === 'relatedEquipment' && row.__SpatialActionMode === 'relatedEquipment');
  }

  return false;
}
